import { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { starterCatalogApi } from '@/services/api';
import IconPicker from '@/components/pos/IconPicker';
import ColorPicker from '@/components/pos/ColorPicker';
import CategoryIcon from '@/components/pos/CategoryIcon';
import { getDefaultCategoryColor } from '@/constants/posCategoryIcons';
import {
  Package, Loader2, Search, Check, ImageOff, ArrowLeft, Download, ChevronDown,
} from 'lucide-react';

type Category = {
  name: string;
  category_name: string;
  industry: string;
  display_order: number;
  image?: string | null;
  product_count: number;
  with_image: number;
};

type CatalogItem = {
  master_product: string;
  item_code: string;
  item_name: string;
  barcode?: string;
  image?: string | null;
  unit_of_measurement?: string;
  default_opening_qty?: number;
  is_weighted?: number;
  plu_code?: string;
  sale_price: number;
  cost_price: number;
  already_imported: boolean;
};

type EditableItem = CatalogItem & {
  selected: boolean;
  sale_price: number;
  cost_price: number;
  wholesale_price: number;
  reseller_price: number;
  minimum_price: number;
  tax_rate: number;
  opening_qty: number;
};

/** Click-to-open popover used for the color/icon pickers so they don't expand inline. */
function PickerPopover({ label, open, onToggle, children }: {
  label: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs border border-gray-200 rounded px-2 py-1.5 bg-white hover:bg-gray-50"
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-3">
          {children}
        </div>
      )}
    </div>
  );
}

export default function StarterCatalogTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [manageStock, setManageStock] = useState(true);
  const [withImages, setWithImages] = useState(true);
  const [defaultTax, setDefaultTax] = useState('');
  const [applyRetailToOthers, setApplyRetailToOthers] = useState(true);
  // category mapping: 'new' = create from master, or existing store category name
  const [categoryTarget, setCategoryTarget] = useState<'new' | string>('new');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('');
  const [colorOpen, setColorOpen] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const [showOthers, setShowOthers] = useState(false);

  const RANDOM_COLORS = ['#EF4444', '#F59E0B', '#22C55E', '#10B981', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'];

  const { data: catData, isLoading: catsLoading } = useQuery({
    queryKey: ['starterCatalogCategories'],
    queryFn: async () => {
      const res = await starterCatalogApi.getCategories();
      return res.data.message as { categories: Category[]; currency: string; store_industry?: string };
    },
  });

  const { data: storeCatsData, refetch: refetchStoreCats } = useQuery({
    queryKey: ['starterStoreCategories'],
    queryFn: async () => {
      const res = await starterCatalogApi.getStoreCategories();
      const msg = res.data.message;
      return (msg?.categories ?? []) as Array<{ name: string; category_name: string; pos_category_color?: string; pos_category_icon?: string }>;
    },
    staleTime: 30_000,
  });

  const categories = useMemo(() => {
    const list = catData?.categories ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (c) => c.category_name.toLowerCase().includes(q) || c.industry.toLowerCase().includes(q),
    );
  }, [catData, search]);

  const storeIndustry = catData?.store_industry || '';
  const searching = Boolean(search.trim());
  const { ownCategories, otherCategories } = useMemo(() => {
    if (searching || !storeIndustry) return { ownCategories: categories, otherCategories: [] as Category[] };
    return {
      ownCategories: categories.filter((c) => c.industry === storeIndustry),
      otherCategories: categories.filter((c) => c.industry !== storeIndustry),
    };
  }, [categories, storeIndustry, searching]);

  const renderCategoryCard = (cat: Category) => (
    <button
      key={cat.name}
      onClick={() => openCategory(cat)}
      className="border border-gray-200 rounded-xl p-4 text-left hover:shadow-md hover:border-emerald-300 transition-all group bg-white"
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
          {cat.industry}
        </span>
        <span className="text-xs text-gray-400">{cat.product_count} {t('starterCatalog.items')}</span>
      </div>
      <p className="font-semibold text-gray-900 group-hover:text-emerald-700">{cat.category_name}</p>
      <p className="text-xs text-gray-500 mt-1">
        {cat.with_image > 0
          ? t('starterCatalog.withImages', { count: cat.with_image })
          : t('starterCatalog.noImages')}
      </p>
    </button>
  );

  const openCategory = async (cat: Category) => {
    setActiveCategory(cat);
    setCategoryTarget('new');
    setNewCategoryName(cat.category_name);
    setNewCategoryColor(RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)]);
    setItems([]);
    try {
      const res = await starterCatalogApi.getItems(cat.name);
      const list = (res.data.message.items as CatalogItem[]).map((it) => ({
        ...it,
        selected: !it.already_imported,
        wholesale_price: it.sale_price,
        reseller_price: it.sale_price,
        minimum_price: it.sale_price,
        tax_rate: 0,
        opening_qty: it.default_opening_qty ?? 0,
      }));
      setItems(list);
    } catch {
      toast.error(t('starterCatalog.loadItemsFailed'));
    }
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      const tax = parseFloat(defaultTax) || 0;
      const selected = items.filter((i) => i.selected && !i.already_imported);
      const payload = selected.map((i) => ({
        master_product: i.master_product,
        item_name: i.item_name,
        sale_price: i.sale_price,
        cost_price: i.cost_price,
        wholesale_price: applyRetailToOthers ? i.sale_price : i.wholesale_price,
        reseller_price: applyRetailToOthers ? i.sale_price : i.reseller_price,
        minimum_price: applyRetailToOthers ? i.sale_price : i.minimum_price,
        tax_rate: i.tax_rate || tax,
        barcode: i.barcode || i.item_code,
        image: withImages ? i.image : null,
        unit_of_measurement: i.unit_of_measurement,
        opening_qty: manageStock ? i.opening_qty : 0,
        is_weighted: i.is_weighted,
        plu_code: i.plu_code,
        category_name: categoryTarget === 'new' ? (newCategoryName || activeCategory?.category_name) : undefined,
        category_color: categoryTarget === 'new' ? newCategoryColor : undefined,
        category_icon: categoryTarget === 'new' ? newCategoryIcon : undefined,
        existing_category: categoryTarget !== 'new' ? categoryTarget : undefined,
      }));
      const res = await starterCatalogApi.importItems({
        items: payload,
        manage_stock: manageStock,
        with_images: withImages,
      });
      return res.data.message as { created: number; skipped: number; stock_entries: number };
    },
    onSuccess: (res) => {
      toast.success(t('starterCatalog.importSuccess', { created: res.created, skipped: res.skipped }));
      void qc.invalidateQueries({ queryKey: ['starterCatalogCategories'] });
      setActiveCategory(null);
      setItems([]);
    },
    onError: () => toast.error(t('starterCatalog.importFailed')),
  });

  const toggleItem = (code: string) =>
    setItems((prev) => prev.map((i) => (i.item_code === code ? { ...i, selected: !i.selected } : i)));

  const updateItem = (code: string, field: keyof EditableItem, value: string) =>
    setItems((prev) =>
      prev.map((i) => (i.item_code === code ? { ...i, [field]: parseFloat(value) || 0 } : i)),
    );

  const selectedCount = items.filter((i) => i.selected && !i.already_imported).length;
  const allSelected = items.length > 0 && items.every((i) => i.already_imported || i.selected);

  return (
    <div className="space-y-4">
      {!activeCategory ? (
        <div className="card p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('starterCatalog.title')}</h2>
            <p className="text-sm text-gray-500">{t('starterCatalog.subtitle')}</p>
          </div>
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('starterCatalog.searchCategories')}
              className="input-field pl-9"
            />
          </div>

          {catsLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : categories.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{t('starterCatalog.noCategories')}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {!searching && storeIndustry && otherCategories.length > 0 && ownCategories.length > 0 && (
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                  {t('starterCatalog.yourIndustry', { industry: storeIndustry })}
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ownCategories.map(renderCategoryCard)}
              </div>

              {otherCategories.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowOthers((v) => !v)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-emerald-700 mb-3"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${showOthers ? 'rotate-180' : ''}`} />
                    {showOthers
                      ? t('starterCatalog.hideOtherCategories')
                      : t('starterCatalog.showOtherCategories', { count: otherCategories.length })}
                  </button>
                  {showOthers && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {otherCategories.map(renderCategoryCard)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100">
            <button
              onClick={() => { setActiveCategory(null); setItems([]); }}
              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-[160px]">
              <h2 className="font-semibold text-gray-900">{activeCategory.category_name}</h2>
              <p className="text-xs text-gray-500">{activeCategory.industry}</p>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={manageStock} onChange={(e) => setManageStock(e.target.checked)} className="rounded" />
              {t('starterCatalog.manageStock')}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} className="rounded" />
              {t('starterCatalog.withImagesOption')}
            </label>
            <button
              onClick={() => importMutation.mutate()}
              disabled={selectedCount === 0 || importMutation.isPending}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {t('starterCatalog.importSelected', { count: selectedCount })}
            </button>
          </div>

          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60 flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={applyRetailToOthers} onChange={(e) => setApplyRetailToOthers(e.target.checked)} className="rounded" />
              {t('starterCatalog.retailForAll')}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              {t('starterCatalog.defaultTax')}
              <input
                type="number" step="0.01" value={defaultTax} onChange={(e) => setDefaultTax(e.target.value)}
                placeholder="0"
                className="w-16 text-right text-xs border border-gray-200 rounded px-1.5 py-1"
              />
              %
            </label>
            <span className="text-xs text-gray-400 ml-auto">
              {t('starterCatalog.selectedCount', { count: selectedCount })}
            </span>
          </div>

          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 bg-gray-50/40">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => setItems((prev) => prev.map((i) => (i.already_imported ? i : { ...i, selected: !allSelected })))}
              className="w-5 h-5 rounded shrink-0 cursor-pointer accent-emerald-600"
            />
            <button
              type="button"
              onClick={() => setItems((prev) => prev.map((i) => (i.already_imported ? i : { ...i, selected: !allSelected })))}
              className="text-sm font-medium text-gray-800 hover:text-emerald-700"
            >
              {allSelected ? t('starterCatalog.deselectAll') : t('starterCatalog.selectAll')}
            </button>
          </div>

          <div className="px-4 py-3 border-b border-gray-100 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium text-gray-600">{t('starterCatalog.importInto')}:</span>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input type="radio" name="catTarget" checked={categoryTarget === 'new'} onChange={() => setCategoryTarget('new')} />
                {t('starterCatalog.newCategory')}
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="radio" name="catTarget"
                  checked={categoryTarget !== 'new'}
                  onChange={() => {
                    void refetchStoreCats();
                    setCategoryTarget(storeCatsData?.[0]?.name || '');
                  }}
                />
                {t('starterCatalog.existingCategory')}
              </label>
              {categoryTarget !== 'new' && (
                <div className="flex items-center gap-2">
                  {(() => {
                    const sel = (storeCatsData ?? []).find((c) => c.name === categoryTarget);
                    const color = sel?.pos_category_color || getDefaultCategoryColor(sel?.category_name || 'C');
                    return <CategoryIcon icon={sel?.pos_category_icon} image="" color={color} size="sm" />;
                  })()}
                  <select
                    value={categoryTarget}
                    onChange={(e) => setCategoryTarget(e.target.value)}
                    className="text-xs border border-gray-200 rounded px-2 py-1 min-w-[160px]"
                  >
                    {(storeCatsData ?? []).length === 0 && (
                      <option value="">{t('common.loading')}</option>
                    )}
                    {(storeCatsData ?? []).map((c) => (
                      <option key={c.name} value={c.name}>{c.category_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {categoryTarget === 'new' && (
              <div className="flex flex-wrap items-end gap-4 pl-1">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('starterCatalog.newCategoryName')}</label>
                  <input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder={t('starterCatalog.newCategoryName')}
                    className="text-xs border border-gray-200 rounded px-2 py-1.5 w-44"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('starterCatalog.categoryColor')}</label>
                  <PickerPopover
                    open={colorOpen}
                    onToggle={() => { setColorOpen((o) => !o); setIconOpen(false); }}
                    label={
                      <span className="flex items-center gap-1.5">
                        <span
                          className="w-4 h-4 rounded-full border border-gray-200 inline-block"
                          style={{ backgroundColor: newCategoryColor || getDefaultCategoryColor(newCategoryName || 'C') }}
                        />
                        {t('starterCatalog.categoryColor')}
                      </span>
                    }
                  >
                    <ColorPicker
                      value={newCategoryColor}
                      onChange={(c) => { setNewCategoryColor(c); setColorOpen(false); }}
                    />
                  </PickerPopover>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('starterCatalog.categoryIcon')}</label>
                  <PickerPopover
                    open={iconOpen}
                    onToggle={() => { setIconOpen((o) => !o); setColorOpen(false); }}
                    label={
                      <span className="flex items-center gap-1.5">
                        <CategoryIcon
                          icon={newCategoryIcon}
                          image=""
                          color={newCategoryColor || getDefaultCategoryColor(newCategoryName || 'C')}
                          size="sm"
                        />
                        {t('starterCatalog.categoryIcon')}
                      </span>
                    }
                  >
                    <IconPicker
                      value={newCategoryIcon}
                      onChange={(n) => { setNewCategoryIcon(n); setIconOpen(false); }}
                      industry={activeCategory?.industry}
                    />
                  </PickerPopover>
                </div>
              </div>
            )}
          </div>

          <div className="max-h-[55vh] overflow-y-auto divide-y divide-gray-50">
            {items.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('common.loading')}
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.item_code}
                  onClick={() => { if (!item.already_imported) toggleItem(item.item_code); }}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    item.already_imported ? 'opacity-50' : 'cursor-pointer hover:bg-emerald-50/50'
                  } ${item.selected && !item.already_imported ? 'bg-emerald-50/60' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={item.selected}
                    disabled={item.already_imported}
                    onChange={() => toggleItem(item.item_code)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-5 h-5 rounded shrink-0 cursor-pointer accent-emerald-600"
                  />
                  {item.image ? (
                    <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <ImageOff className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {item.barcode}
                      {item.already_imported && (
                        <span className="ml-2 text-emerald-600 font-medium">
                          <Check className="inline w-3 h-3 -mt-0.5" /> {t('starterCatalog.alreadyImported')}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="text-right">
                      <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.costPrice')}</label>
                      <input type="number" step="0.01" value={item.cost_price} onChange={(e) => updateItem(item.item_code, 'cost_price', e.target.value)} className="w-20 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                    </div>
                    <div className="text-right">
                      <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.salePrice')}</label>
                      <input type="number" step="0.01" value={item.sale_price} onChange={(e) => updateItem(item.item_code, 'sale_price', e.target.value)} className="w-20 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                    </div>
                    {!applyRetailToOthers && (
                      <>
                        <div className="text-right">
                          <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.wholesalePrice')}</label>
                          <input type="number" step="0.01" value={item.wholesale_price} onChange={(e) => updateItem(item.item_code, 'wholesale_price', e.target.value)} className="w-20 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                        </div>
                        <div className="text-right">
                          <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.resellerPrice')}</label>
                          <input type="number" step="0.01" value={item.reseller_price} onChange={(e) => updateItem(item.item_code, 'reseller_price', e.target.value)} className="w-20 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                        </div>
                        <div className="text-right">
                          <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.minimumPrice')}</label>
                          <input type="number" step="0.01" value={item.minimum_price} onChange={(e) => updateItem(item.item_code, 'minimum_price', e.target.value)} className="w-20 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                        </div>
                      </>
                    )}
                    <div className="text-right">
                      <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.taxRate')}</label>
                      <input type="number" step="0.01" value={item.tax_rate} onChange={(e) => updateItem(item.item_code, 'tax_rate', e.target.value)} className="w-16 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                    </div>
                    {manageStock && (
                      <div className="text-right">
                        <label className="block text-xs font-semibold text-gray-900">{t('starterCatalog.openingQty')}</label>
                        <input type="number" step="0.01" value={item.opening_qty} onChange={(e) => updateItem(item.item_code, 'opening_qty', e.target.value)} className="w-16 text-right text-sm border border-gray-200 rounded px-1.5 py-1" />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
